/**
 * Push (T68): registration after sign-in, the routing of a tapped
 * notification, the foreground toast, and forgetting the device on sign-out.
 *
 * What the rest of the app imports:
 * - `<PushRouting />`, mounted once by the router shell inside `SessionGate`
 *   (T10's `PushBridge`), so it runs signed in only. It is a COMPONENT, not a
 *   hook: since T46 the rationale dialog and the foreground toast render in
 *   the app tree, under the one `ToastProvider` above the gate, instead of in
 *   a React root of their own;
 * - `usePush()` → `{ state, enable, disable }`, the profile's notification
 *   switch (T51);
 * - `useSignOutWithPush()`, T47's `useSignOut` with the unregister step
 *   (`usePushSignOut`, `use-push.ts`) already in front (T51's account
 *   section).
 *
 * Importing this module registers the native tap and receipt listeners
 * (`startPushListeners`), before the router mounts, so a tap that cold-starts
 * the app is held until the router is ready. On the web that is a no-op.
 */
import type { ReactNode } from "react";
import { PushLayer } from "./push-layer";
import { startPushListeners } from "./runtime";
import { usePushRouting } from "./use-push";

startPushListeners();

/** The routing hook plus what it draws. `null` on the web (`usePushRouting`). */
export function PushRouting(): ReactNode {
  const layer = usePushRouting();
  if (layer === null) {
    return null;
  }
  return <PushLayer actions={layer.actions} store={layer.store} />;
}

export { usePush, useSignOutWithPush } from "./use-push";
